import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Sparkles, X, Send, Loader2, ArrowRight, Mic, Volume2, VolumeX, Phone, PhoneOff } from "lucide-react";

const SERVICE_LABEL = {
  restaurants: { en: "Restaurants", ar: "المطاعم" },
  home_services: { en: "Home Services", ar: "خدمات منزلية" },
  courier: { en: "Courier", ar: "التوصيل" },
  pharmacy: { en: "Pharmacy", ar: "الصيدلية" },
  grocery: { en: "Grocery", ar: "البقالة" },
  hotels: { en: "Hotels", ar: "الفنادق" },
  flats: { en: "Flats", ar: "الشقق" },
  business: { en: "Business", ar: "الأعمال" },
  ride_hailing: { en: "Serv واصل", ar: "سيرف واصل" },
};

const getSession = () => {
  let s = localStorage.getItem("so_ai_session");
  if (!s) { s = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; localStorage.setItem("so_ai_session", s); }
  return s;
};

export const AiAssistant = () => {
  const { lang } = useLang();
  const navigate = useNavigate();
  const isAr = lang === "ar";
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSession());
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [speakerOn, setSpeakerOn] = useState(true);
  const mediaRec = useRef(null);
  const chunks = useRef([]);
  const audioRef = useRef(null);
  // hands-free live-call refs
  const [callActive, setCallActive] = useState(false);
  const [callStatus, setCallStatus] = useState("idle"); // listening|transcribing|thinking|speaking
  const callActiveRef = useRef(false);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const callRecRef = useRef(null);
  const callChunks = useRef([]);
  const rafRef = useRef(null);
  const hasSpeechRef = useRef(false);
  const silenceStartRef = useRef(null);
  const segStartRef = useRef(0);

  const greeting = isAr
    ? "أهلاً! أنا مساعد ServeOn. اضغط زر الهاتف ☎️ وابدأ مكالمة صوتية — اتكلم وأنا أرد عليك على طول من غير ما تضغط، وقول محتاج شنو. 🍔🛺🏠"
    : "Hi! I'm your ServeOn assistant. Tap the phone ☎️ to start a live voice call — just talk and I'll reply hands-free. Or type/tap the mic anytime. 🍔🛺🏠";

  useEffect(() => {
    if (open && messages.length === 0) setMessages([{ role: "assistant", text: greeting }]);
  }, [open]); // eslint-disable-line

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading, callStatus]);

  useEffect(() => () => {
    callActiveRef.current = false;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) {}
    try { audioCtxRef.current?.close(); } catch (_) {}
    try { audioRef.current?.pause(); } catch (_) {}
  }, []);

  const stopAudio = () => { try { audioRef.current?.pause(); } catch (_) {} audioRef.current = null; };

  const playReply = async (text) => {
    if (!speakerOn || !text) return;
    try {
      const res = await api.post("/ai/tts", { text }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      stopAudio();
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => URL.revokeObjectURL(url);
      a.play().catch(() => {});
    } catch (_) { /* audio optional */ }
  };

  const sendMessage = async (text) => {
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: sessionId.current, message: text });
      setMessages((m) => [...m, { role: "assistant", text: data.reply, service: data.suggested_service }]);
      playReply(data.reply);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: isAr ? "المساعد مشغول حالياً، جرّب تاني." : "The assistant is busy, please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  const send = () => {
    const t = input.trim();
    if (!t) return;
    setInput("");
    sendMessage(t);
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const r = new MediaRecorder(stream, { mimeType: mime });
      chunks.current = [];
      r.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
      r.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks.current, { type: mime });
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "recording.webm");
          const { data } = await api.post("/ai/transcribe", fd);
          const text = (data.text || "").trim();
          if (text) sendMessage(text);
          else setMessages((m) => [...m, { role: "assistant", text: isAr ? "ما سمعت حاجة، جرّب تاني." : "I didn't catch that, please try again." }]);
        } catch (e) {
          setMessages((m) => [...m, { role: "assistant", text: isAr ? "ما قدرت أفرّغ الصوت، جرّب تاني." : "Couldn't transcribe that, please try again." }]);
        } finally {
          setTranscribing(false);
        }
      };
      mediaRec.current = r;
      r.start();
      setRecording(true);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: isAr ? "محتاجين إذن الميكروفون عشان تتكلم." : "Microphone permission is needed to speak." }]);
    }
  };

  const stopRec = () => { mediaRec.current?.stop(); setRecording(false); };
  const toggleRec = () => (recording ? stopRec() : startRec());

  // ── Hands-free live voice conversation (VAD auto turn-taking) ──
  const playReplyAwait = (text) => new Promise(async (resolve) => {
    if (!text) return resolve();
    try {
      const res = await api.post("/ai/tts", { text }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      stopAudio();
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => { URL.revokeObjectURL(url); resolve(); };
      a.onerror = () => resolve();
      a.play().catch(() => resolve());
    } catch (_) { resolve(); }
  });

  const monitorVAD = () => {
    if (!callActiveRef.current) return;
    const analyser = analyserRef.current;
    if (!analyser) return;
    const buf = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
    const rms = Math.sqrt(sum / buf.length);
    const now = Date.now();
    if (rms > 0.045) { hasSpeechRef.current = true; silenceStartRef.current = null; }
    else if (hasSpeechRef.current) {
      if (silenceStartRef.current == null) silenceStartRef.current = now;
      else if (now - silenceStartRef.current > 1100) { finishSegment(); return; }
    }
    if (hasSpeechRef.current && now - segStartRef.current > 15000) { finishSegment(); return; }
    rafRef.current = requestAnimationFrame(monitorVAD);
  };

  const finishSegment = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    try { if (callRecRef.current && callRecRef.current.state === "recording") callRecRef.current.stop(); } catch (_) {}
  };

  const listenSegment = () => {
    if (!callActiveRef.current || !streamRef.current) return;
    const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
    const r = new MediaRecorder(streamRef.current, { mimeType: mime });
    callChunks.current = [];
    r.ondataavailable = (e) => { if (e.data.size) callChunks.current.push(e.data); };
    r.onstop = () => onSegmentStop(mime);
    callRecRef.current = r;
    hasSpeechRef.current = false; silenceStartRef.current = null; segStartRef.current = Date.now();
    r.start();
    setCallStatus("listening");
    rafRef.current = requestAnimationFrame(monitorVAD);
  };

  const onSegmentStop = async (mime) => {
    if (!callActiveRef.current) return;
    if (!hasSpeechRef.current) { listenSegment(); return; }
    const blob = new Blob(callChunks.current, { type: mime });
    setCallStatus("transcribing");
    try {
      const fd = new FormData();
      fd.append("audio", blob, "segment.webm");
      const { data } = await api.post("/ai/transcribe", fd);
      const text = (data.text || "").trim();
      if (!text) { if (callActiveRef.current) listenSegment(); return; }
      setMessages((m) => [...m, { role: "user", text }]);
      setCallStatus("thinking");
      const { data: chat } = await api.post("/ai/chat", { session_id: sessionId.current, message: text });
      setMessages((m) => [...m, { role: "assistant", text: chat.reply, service: chat.suggested_service }]);
      if (!callActiveRef.current) return;
      setCallStatus("speaking");
      await playReplyAwait(chat.reply);
      if (callActiveRef.current) listenSegment();
    } catch (e) {
      if (callActiveRef.current) listenSegment();
    }
  };

  const startCall = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      streamRef.current = stream;
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      try { await ctx.resume(); } catch (_) {}
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      src.connect(analyser);
      audioCtxRef.current = ctx; analyserRef.current = analyser;
      callActiveRef.current = true; setCallActive(true);
      listenSegment();
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: isAr ? "محتاجين إذن الميكروفون عشان نبدأ المكالمة." : "Microphone permission is needed to start the call." }]);
    }
  };

  const endCall = () => {
    callActiveRef.current = false; setCallActive(false); setCallStatus("idle");
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    try { if (callRecRef.current && callRecRef.current.state !== "inactive") callRecRef.current.stop(); } catch (_) {}
    stopAudio();
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) {}
    try { audioCtxRef.current?.close(); } catch (_) {}
    streamRef.current = null; audioCtxRef.current = null; analyserRef.current = null;
  };

  const closePanel = () => { stopAudio(); if (recording) stopRec(); if (callActiveRef.current) endCall(); setOpen(false); };

  const callStatusLabel = {
    listening: isAr ? "أسمعك… اتكلم" : "Listening… speak",
    transcribing: isAr ? "بفهم كلامك…" : "Understanding…",
    thinking: isAr ? "بفكّر…" : "Thinking…",
    speaking: isAr ? "برد عليك…" : "Speaking…",
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="ai-assistant-fab"
          className="fixed z-50 bottom-24 end-4 w-14 h-14 rounded-full flex items-center justify-center text-white shadow-lg active:scale-95 transition-transform"
          style={{ background: "var(--so-orange)", boxShadow: "0 8px 20px -6px rgba(0,0,0,0.3)" }}
          aria-label="AI assistant"
        >
          <Sparkles className="w-6 h-6" />
        </button>
      )}

      {open && (
        <div className="fixed z-50 bottom-24 end-4 w-[calc(100vw-2rem)] max-w-sm rounded-2xl overflow-hidden border border-[var(--so-border)] bg-[var(--so-surface)] shadow-2xl flex flex-col" style={{ height: "min(70vh, 560px)" }} data-testid="ai-assistant-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 text-white" style={{ background: "var(--so-orange)" }}>
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              <div>
                <div className="font-bold text-sm leading-none">{isAr ? "مساعد ServeOn" : "ServeOn AI"}</div>
                <div className="text-[11px] text-white/85 mt-0.5">{isAr ? "قول ليك محتاج شنو" : "You describe. We handle."}</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => (callActive ? endCall() : startCall())} data-testid="ai-call-toggle"
                className={`w-8 h-8 rounded-full flex items-center justify-center ${callActive ? "bg-white text-[var(--so-orange)]" : "hover:bg-white/20"}`} aria-label="voice call">
                {callActive ? <PhoneOff className="w-4 h-4" /> : <Phone className="w-4 h-4" />}
              </button>
              <button onClick={() => { const n = !speakerOn; setSpeakerOn(n); if (!n) stopAudio(); }} data-testid="ai-speaker-toggle"
                className="w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center" aria-label="toggle voice reply">
                {speakerOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </button>
              <button onClick={closePanel} data-testid="ai-close-btn" className="w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
          </div>

          {/* Live-call status strip */}
          {callActive && (
            <div className="flex items-center justify-center gap-2 py-2 text-sm font-semibold border-b border-[var(--so-border)]"
              style={{ background: "var(--so-bg)", color: "var(--so-orange)" }} data-testid="ai-call-status">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ background: "var(--so-orange)" }} />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: "var(--so-orange)" }} />
              </span>
              {callStatusLabel[callStatus] || (isAr ? "مكالمة نشطة" : "On call")}
            </div>
          )}

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3" dir={isAr ? "rtl" : "ltr"} data-testid="ai-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-[var(--so-orange)] text-white rounded-br-sm" : "bg-[var(--so-bg)] border border-[var(--so-border)] rounded-bl-sm"}`}>
                  <div className="whitespace-pre-wrap">{m.text}</div>
                  {m.service && SERVICE_LABEL[m.service] && (
                    <button
                      onClick={() => { setOpen(false); navigate(m.service === "restaurants" ? "/app" : `/app/service/${m.service}`); }}
                      data-testid={`ai-goto-${m.service}`}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full bg-[var(--so-orange)] text-white hover:brightness-110"
                    >
                      {isAr ? "افتح" : "Open"} {isAr ? SERVICE_LABEL[m.service].ar : SERVICE_LABEL[m.service].en}
                      <ArrowRight className={`w-3.5 h-3.5 ${isAr ? "rotate-180" : ""}`} />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start"><div className="bg-[var(--so-bg)] border border-[var(--so-border)] rounded-2xl rounded-bl-sm px-3.5 py-2.5"><Loader2 className="w-4 h-4 animate-spin text-[var(--so-orange)]" /></div></div>
            )}
          </div>

          {/* Input */}
          <div className="p-2.5 border-t border-[var(--so-border)] flex items-center gap-2" dir={isAr ? "rtl" : "ltr"}>
            <button
              onClick={toggleRec}
              disabled={transcribing || loading || callActive}
              data-testid="ai-mic-btn"
              className={`w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center transition-colors disabled:opacity-50 ${recording ? "bg-red-500 text-white animate-pulse" : "bg-[var(--so-bg)] border border-[var(--so-border)] text-[var(--so-text-soft)]"}`}
              aria-label="speak"
            >
              {transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
            </button>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              placeholder={callActive ? (isAr ? "المكالمة شغّالة… اتكلم" : "Call active… just speak") : recording ? (isAr ? "بتكلم… اضغط لإيقاف" : "Listening… tap mic to stop") : transcribing ? (isAr ? "بحوّل كلامك…" : "Transcribing…") : (isAr ? "اكتب أو اتكلم…" : "Type or speak…")}
              disabled={recording || transcribing || callActive}
              className="so-input flex-1"
              style={{ padding: "0.6rem 0.9rem" }}
              data-testid="ai-input"
            />
            <button onClick={send} disabled={loading || !input.trim()} data-testid="ai-send-btn"
              className="w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center text-white disabled:opacity-50" style={{ background: "var(--so-orange)" }}>
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
